import {
  ColumnDef,
  ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
  Updater,
  PaginationState,
} from '@tanstack/react-table'
import Fuse, { FuseOptionKey } from 'fuse.js'
import { ChevronDown, Loader2, Settings } from 'lucide-react'
import { atom, useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import * as React from 'react'

import { Button } from './button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'
import { Input } from './input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from './table'
import { HeaderCell } from './sortable-header-cell'
import { cn } from '@/lib/utils'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Switch } from './switch'

// Constants
const SEARCH_DEBOUNCE_MS = 300
const PAGINATION_DEBOUNCE_MS = 150
const MIN_SEARCH_LENGTH = 2
const DEFAULT_PAGE_SIZE = 10
const EXCLUDED_VISIBILITY_COLUMNS = ['state', 'edit'] as const
const EXCLUDED_SORTABLE_COLUMNS = ['state', 'edit'] as const

const FUSE_OPTIONS = {
  threshold: 0.3,
  includeScore: true,
  includeMatches: false,
  ignoreLocation: true,
  minMatchCharLength: MIN_SEARCH_LENGTH,
  isCaseSensitive: false,
  useExtendedSearch: false,
  findAllMatches: true,
  shouldSort: true,
} as const

// Extend ColumnMeta to include displayName, name, and sortingStrategy
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    displayName?: string
    name?: string
    sortingStrategy?: 'server' | 'client' | 'none' // 'server' = server-side sort (default), 'client' = client-side sort on current page, 'none' = no sorting
  }
}

// Helper function to get column display name

/**
 * Merge a previous column order with the current list of columns / visibility.
 *
 * The goal is to
 * 1. keep the relative ordering that the user explicitly created via drag/drop
 * 2. honour fixed "state" / "edit" slots at the start / end
 * 3. insert any newly‑introduced columns into their natural position according
 *    to the `columns` array instead of indiscriminately appending them at the
 *    end (which is what we observed during page navigation).
 */
export function mergeColumnOrder(
  prevOrder: string[],
  columns: ColumnDef<any, any>[],
  visibility: VisibilityState | undefined
): string[] {
  const ids = columns
    .map((col) => {
      if ('id' in col && typeof col.id === 'string') return col.id
      if ('accessorKey' in col && typeof col.accessorKey === 'string') return col.accessorKey
      return ''
    })
    .filter(Boolean) as string[]

  const visibleIds = ids.filter((id) => visibility?.[id] !== false)

  const hasState = visibleIds.includes('state')
  const hasEdit = visibleIds.includes('edit')
  const middle = visibleIds.filter((id) => id !== 'state' && id !== 'edit')

  const prevIndex = new Map(prevOrder.map((id, idx) => [id, idx]))

  const sortedMiddle = middle.slice().sort((a, b) => {
    const aIn = prevIndex.has(a)
    const bIn = prevIndex.has(b)

    if (aIn && bIn) {
      return (prevIndex.get(a)! as number) - (prevIndex.get(b)! as number)
    }

    // If neither value existed before, respect the original column array order
    if (!aIn && !bIn) {
      return middle.indexOf(a) - middle.indexOf(b)
    }

    // One value was present previously while the other is new.  Instead of
    // forcing the old one to win (which would push new columns to the end),
    // fall back to the natural visibleIds order.  This allows new entries to
    // land where they belong relative to their siblings.
    return middle.indexOf(a) - middle.indexOf(b)
  })

  const result: string[] = []
  if (hasState) result.push('state')
  result.push(...sortedMiddle)
  if (hasEdit) result.push('edit')
  return result
}

function getColumnDisplayName(column: any): string {
  return (
    column.columnDef.meta?.name ||
    column.columnDef.meta?.displayName ||
    (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)
  )
}

/**
 * DataTable - A flexible table component with client-side and server-side modes
 *
 * LOADING STATES:
 * - `isFetching`: External prop indicating server data is being fetched
 *   - Shows spinner in search input when `isFetching && searchValue.length >= 1`
 *   - Prevents empty state flash during pagination/search by showing empty cell
 *   - Use this for ALL server-side operations (search, pagination, mutations)
 *
 * CLIENT-SIDE MODE (default):
 * - Uses Fuse.js for local search/filtering
 * - `searchKeys` prop enables search functionality
 * - Data is filtered locally with 300ms debounce
 * - Pagination is client-side (built into TanStack Table)
 *
 * SERVER-SIDE MODE:
 * - `serverSideSearch={true}`: Disables Fuse.js, calls `onSearchChange` with debounced value
 * - `manualPagination={true}`: Requires `pageCount` prop, calls `onPageChange` on navigation
 * - Pass `isFetching={true}` when your query is loading/fetching
 * - Component handles empty state visibility to prevent flashing during transitions
 *
 * EXAMPLE:
 * ```tsx
 * const { data, isFetching } = useQuery({ queryKey: ['items', page, search], ... })
 * <DataTable
 *   data={data}
 *   serverSideSearch
 *   manualPagination
 *   pageCount={totalPages}
 *   isFetching={isFetching}
 *   onSearchChange={setSearch}
 *   onPageChange={setPage}
 * />
 * ```
 */

// Core props
interface DataTableCoreProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  emptyState?: React.ReactNode
}

// Search props
interface DataTableSearchProps<TData> {
  searchKeys?: FuseOptionKey<TData>[]
  searchPlaceholder?: string
  persistSearch?: boolean
  searchStorageKey?: string
  initialSearchValue?: string
  serverSideSearch?: boolean
  onSearchChange?: (search: string) => void
}

// Pagination props
interface DataTablePaginationProps {
  showPagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  initialPageIndex?: number
  manualPagination?: boolean
  pageCount?: number
  totalCount?: number // Total number of records for manual pagination
  onPageChange?: (pageIndex: number) => void
  onPageSizeChange?: (pageSize: number) => void
}

// Sorting props
interface DataTableSortingProps {
  serverSideSorting?: boolean
  initialSorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
}

// Column props
interface DataTableColumnProps {
  showColumnVisibility?: boolean
  columnVisibilityButtonText?: string
  columnVisibilityDropdownClassName?: string
  initialColumnVisibility?: VisibilityState
  onColumnVisibilityChange?: (visibility: VisibilityState) => void
  enableColumnResizing?: boolean
  initialColumnSizing?: ColumnSizingState
  onColumnSizingChange?: (sizing: ColumnSizingState) => void
  columnSizingStorageKey?: string
  enableColumnReordering?: boolean
  columnOrder?: string[]
  onColumnOrderChange?: (columnOrder: string[]) => void
  showDateTimeToggle?: boolean
  dateTimeToggleLabel?: string
  dateTimeToggleValue?: boolean
  onDateTimeToggleChange?: (value: boolean) => void
}

// Row props
interface DataTableRowProps<TData> {
  onRowClick?: (row: TData) => void
  rowClassName?: (row: TData) => string
}

// Loading props
interface DataTableLoadingProps {
  isFetching?: boolean
}

// Style props
interface DataTableStyleProps {
  className?: string
  tableWrapperClassName?: string
  tableClassName?: string
  alternateRowColors?: boolean
}

// Layout props - NEW
type ControlPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'none'

interface DataTableControls {
  search: React.ReactNode
  columnVisibility: React.ReactNode
  columnSettings: React.ReactNode
  pageSize: React.ReactNode
  pagination: React.ReactNode
  totalCount: React.ReactNode
}

interface DataTableLayoutProps {
  /**
   * Preset layout configurations:
   * - 'default': Search top-left, column visibility top-right, pagination bottom-right, count bottom-left
   * - 'compact': All controls in top bar
   * - 'toolbar': Controls in a single toolbar at top
   * - 'split': Search top-left, controls top-right, pagination bottom-right
   * - 'custom': Use customHeader/customFooter for full control
   */
  controlsLayout?: 'default' | 'compact' | 'toolbar' | 'split' | 'custom'

  // Individual control positioning (overrides preset layout)
  searchPosition?: ControlPosition
  columnVisibilityPosition?: ControlPosition
  columnSettingsPosition?: ControlPosition
  pageSizePosition?: ControlPosition
  paginationPosition?: ControlPosition
  totalCountPosition?: ControlPosition

  // Custom slots for maximum flexibility
  // Can be a ReactNode or a function that receives all controls for custom arrangement
  customHeader?: React.ReactNode | ((controls: DataTableControls) => React.ReactNode)
  customFooter?: React.ReactNode

  // Hide default controls when using custom slots
  hideDefaultControls?: boolean
}

type DataTableProps<TData, TValue> = DataTableCoreProps<TData, TValue> &
  DataTableSearchProps<TData> &
  DataTablePaginationProps &
  DataTableSortingProps &
  DataTableColumnProps &
  DataTableRowProps<TData> &
  DataTableLoadingProps &
  DataTableStyleProps &
  DataTableLayoutProps

export function DataTable<TData, TValue>({
  // Core
  columns,
  data,
  emptyState,
  // Search
  searchKeys,
  searchPlaceholder = 'Suchen...',
  persistSearch = false,
  searchStorageKey,
  initialSearchValue,
  serverSideSearch = false,
  onSearchChange,
  // Pagination
  showPagination = true,
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions,
  initialPageIndex = 0,
  manualPagination = false,
  pageCount,
  totalCount,
  onPageChange,
  onPageSizeChange,
  // Sorting
  serverSideSorting = false,
  initialSorting,
  onSortingChange,
  // Columns
  showColumnVisibility = true,
  columnVisibilityButtonText = 'Spalten',
  columnVisibilityDropdownClassName,
  initialColumnVisibility,
  onColumnVisibilityChange,
  enableColumnResizing = false,
  initialColumnSizing,
  onColumnSizingChange,
  columnSizingStorageKey,
  enableColumnReordering = false,
  columnOrder,
  onColumnOrderChange,
  showDateTimeToggle = false,
  dateTimeToggleLabel = 'Datum und Uhrzeit',
  dateTimeToggleValue = false,
  onDateTimeToggleChange,
  // Rows
  onRowClick,
  rowClassName,
  // Loading
  isFetching = false,
  // Style
  className,
  tableWrapperClassName,
  tableClassName,
  alternateRowColors = false,
  // Layout
  controlsLayout = 'default',
  searchPosition,
  columnVisibilityPosition,
  columnSettingsPosition,
  pageSizePosition,
  paginationPosition,
  totalCountPosition,
  customHeader,
  customFooter,
  hideDefaultControls = false,
}: DataTableProps<TData, TValue>) {
  // Validation: server-side pagination requires pageCount
  if (manualPagination && pageCount === undefined) {
    console.warn('DataTable: manualPagination=true requires pageCount prop')
  }

  // Use transition for non-urgent updates (search, filter, sort)
  const [isPending, startTransition] = React.useTransition()

  // Table state
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting || [])

  // Initialize columnVisibility only once - use useState initializer function
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialColumnVisibility || {}
  )

  const [initialColumnVisibilityState, setInitialColumnVisibilityState] =
    React.useState<VisibilityState>({})

  // Initialize columnSizing - always persist when resizing is enabled
  const columnSizingAtom = React.useMemo(() => {
    if (enableColumnResizing) {
      const storageKey = columnSizingStorageKey || 'data-table-column-sizing'
      return atomWithStorage(storageKey, initialColumnSizing || {})
    }
    return atom(initialColumnSizing || {})
  }, [enableColumnResizing, columnSizingStorageKey, initialColumnSizing])

  const [columnSizing, setColumnSizing] = useAtom(columnSizingAtom)

  const [internalColumnOrder, setInternalColumnOrder] = React.useState<string[]>(columnOrder || [])

  React.useEffect(() => {
    if (columnOrder) {
      setInternalColumnOrder(mergeColumnOrder(columnOrder, columns, columnVisibility))
    }
  }, [columnOrder, columns, columnVisibility])

  // Unified setter: calls parent callback and updates local state (if uncontrolled)
  const updateColumnOrder = React.useCallback(
    (order: string[]) => {
      onColumnOrderChange?.(order)
      setInternalColumnOrder(order)
    },
    [onColumnOrderChange]
  )

  // Search state - use initialSearchValue if provided, otherwise use storage/atom
  const searchAtom = React.useMemo(() => {
    if (persistSearch && searchStorageKey) {
      return atomWithStorage(searchStorageKey, '')
    }
    return atom('')
  }, [persistSearch, searchStorageKey])

  const [searchValue, setSearchValue] = useAtom(searchAtom)

  // Sync columnVisibility with initialColumnVisibility when it changes
  React.useEffect(() => {
    if (initialColumnVisibility && Object.keys(initialColumnVisibility).length > 0) {
      setColumnVisibility(initialColumnVisibility)
    }
  }, [initialColumnVisibility])

  // Initialize column order with visible columns on mount
  // Initialize/reset column order when columns or visibility change (uncontrolled only)
  React.useEffect(() => {
    if (columnOrder !== undefined) {
      // parent is controlling order, don't override
      return
    }
    setInternalColumnOrder((prevOrder) =>
      mergeColumnOrder(prevOrder, columns, initialColumnVisibility)
    )
  }, [columns, initialColumnVisibility, columnOrder])

  // Sync searchValue with initialSearchValue when it changes (URL-based mode)
  React.useEffect(() => {
    if (initialSearchValue !== undefined) {
      setSearchValue(initialSearchValue)
    }
  }, [initialSearchValue, setSearchValue])

  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('')

  // State to prevent loading flash and track initial load
  const [displayData, setDisplayData] = React.useState<TData[]>(data)
  const [isInitialLoad, setIsInitialLoad] = React.useState(true)

  // Update display data when loading completes
  React.useEffect(() => {
    if (!isFetching && !isPending) {
      setDisplayData(data)
      setIsInitialLoad(false)
    }
  }, [data, isFetching, isPending])

  // Debounce search value
  React.useEffect(() => {
    if (serverSideSearch && onSearchChange) {
      const timer = setTimeout(() => {
        onSearchChange(searchValue)
      }, SEARCH_DEBOUNCE_MS)
      return () => clearTimeout(timer)
    } else {
      const timer = setTimeout(() => {
        startTransition(() => {
          setDebouncedSearchValue(searchValue)
        })
      }, SEARCH_DEBOUNCE_MS)
      return () => clearTimeout(timer)
    }
  }, [searchValue, serverSideSearch, onSearchChange])

  // Filtered data using Fuse.js (only in client-side mode)
  const filteredData = React.useMemo(() => {
    // Server-side search: use data as-is (already filtered by server)
    if (serverSideSearch) {
      return displayData
    }

    // Client-side search: use Fuse.js
    const trimmedSearch = debouncedSearchValue.trim()
    if (
      !trimmedSearch ||
      trimmedSearch.length < MIN_SEARCH_LENGTH ||
      !searchKeys ||
      searchKeys.length === 0
    ) {
      return displayData
    }

    const fuse = new Fuse(displayData, { ...FUSE_OPTIONS, keys: searchKeys })
    return fuse.search(trimmedSearch).map((result) => result.item)
  }, [displayData, debouncedSearchValue, searchKeys, serverSideSearch])

  // Pagination state
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize,
  })

  // Track last URL update to prevent duplicate updates
  const lastURLUpdate = React.useRef<number>(initialPageIndex)

  // Initialize pagination from URL
  React.useEffect(() => {
    if (initialPageIndex !== pagination.pageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: initialPageIndex }))
      lastURLUpdate.current = initialPageIndex
    }
  }, [initialPageIndex])

  // Debounced URL update effect
  React.useEffect(() => {
    // Skip if page hasn't actually changed
    if (lastURLUpdate.current === pagination.pageIndex) {
      return
    }

    // Debounce URL updates to prevent rapid successive calls
    const timer = setTimeout(() => {
      lastURLUpdate.current = pagination.pageIndex
      onPageChange?.(pagination.pageIndex)
    }, PAGINATION_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [pagination.pageIndex, onPageChange])

  // Handle pagination changes
  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    setPagination(newPagination)
  }

  // Handle column sizing changes with callback
  const handleColumnSizingChange = React.useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater
      setColumnSizing(newSizing)
      if (onColumnSizingChange) {
        onColumnSizingChange(newSizing)
      }
    },
    [columnSizing, onColumnSizingChange]
  )

  // Handle sorting changes with callback (supports hybrid sorting)
  const handleSortingChange = React.useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      setSorting(newSorting)

      // Always notify parent of sorting changes (for URL state)
      if (serverSideSorting && onSortingChange) {
        onSortingChange(newSorting)
      }
    },
    [sorting, serverSideSorting, onSortingChange]
  )

  // Handle column visibility changes - only update local state, don't call parent callback
  const handleColumnVisibilityChange = React.useCallback(
    (updater: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVisibility = typeof updater === 'function' ? updater(prev) : updater
        setInternalColumnOrder((prevOrder) => mergeColumnOrder(prevOrder, columns, newVisibility))
        return newVisibility
      })
    },
    [columns]
  )

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onPaginationChange: handlePaginationChange,
    onColumnSizingChange: handleColumnSizingChange,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    autoResetPageIndex: false,
    manualPagination,
    pageCount,
    manualSorting: serverSideSorting || false,
    initialState: {
      pagination: {
        pageSize,
        pageIndex: initialPageIndex,
      },
    },
    state: {
      sorting,
      columnVisibility,
      pagination,
      columnSizing,
      columnOrder: internalColumnOrder,
    },
  })

  // Helper: Determine control positions based on layout
  const getControlPositions = () => {
    // If hideDefaultControls is true, hide all controls regardless of layout
    if (hideDefaultControls) {
      return {
        search: 'none',
        columnVisibility: 'none',
        columnSettings: 'none',
        pageSize: 'none',
        pagination: 'none',
        totalCount: 'none',
      } as const
    }

    // Apply preset layouts if no individual overrides
    if (
      !searchPosition &&
      !columnVisibilityPosition &&
      !columnSettingsPosition &&
      !pageSizePosition &&
      !paginationPosition &&
      !totalCountPosition
    ) {
      switch (controlsLayout) {
        case 'compact':
          return {
            search: 'top-left',
            columnVisibility: 'top-right',
            columnSettings: 'top-right',
            pageSize: 'top-right',
            pagination: 'top-right',
            totalCount: 'top-right',
          } as const
        case 'toolbar':
          return {
            search: 'top-left',
            columnVisibility: 'top-right',
            columnSettings: 'top-right',
            pageSize: 'top-right',
            pagination: 'top-right',
            totalCount: 'bottom-left',
          } as const
        case 'split':
          return {
            search: 'top-left',
            columnVisibility: 'top-right',
            columnSettings: 'top-right',
            pageSize: 'top-right',
            pagination: 'bottom-right',
            totalCount: 'bottom-left',
          } as const
        case 'custom':
          return {
            search: 'top-left',
            columnVisibility: 'top-right',
            pageSize: 'bottom-left',
            pagination: 'bottom-right',
            totalCount: 'bottom-left',
          } as const
        case 'default':
        default:
          return {
            search: 'top-left',
            columnVisibility: 'top-right',
            columnSettings: 'top-right',
            pageSize: 'bottom-left',
            pagination: 'bottom-right',
            totalCount: 'bottom-left',
          } as const
      }
    }

    // Individual positions override preset layouts
    return {
      search: searchPosition || 'top-left',
      columnVisibility: columnVisibilityPosition || 'top-right',
      columnSettings: columnSettingsPosition || 'top-right',
      pageSize: pageSizePosition || 'bottom-left',
      pagination: paginationPosition || 'bottom-right',
      totalCount: totalCountPosition || 'bottom-left',
    } as const
  }

  const positions = getControlPositions()

  // Render helper: Search control
  const renderSearch = () => {
    if (!searchKeys || searchKeys.length === 0 || positions.search === 'none') return null
    return (
      <div className="relative w-full max-w-sm">
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value)
            if (pagination.pageIndex !== 0) {
              table.setPageIndex(0)
            }
          }}
          className="w-full"
        />
        {(isFetching || isPending) && searchValue.length >= 1 && (
          <Loader2 className="text-muted-foreground absolute right-2 top-2 h-4 w-4 animate-spin" />
        )}
      </div>
    )
  }

  // Render helper: Column visibility control
  const renderColumnVisibility = () => {
    if (!showColumnVisibility || positions.columnVisibility === 'none') return null
    return (
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) {
            setInitialColumnVisibilityState({ ...columnVisibility })
          } else if (onColumnVisibilityChange) {
            const hasChanged =
              JSON.stringify(initialColumnVisibilityState) !== JSON.stringify(columnVisibility)
            if (hasChanged) {
              onColumnVisibilityChange(columnVisibility)
            }
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="!shadow-none">
            {columnVisibilityButtonText} <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={columnVisibilityDropdownClassName}>
          {table
            .getAllColumns()
            .filter((column) => !EXCLUDED_VISIBILITY_COLUMNS.includes(column.id as any))
            .map((column) => {
              const displayName = getColumnDisplayName(column)
              return (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={columnVisibility[column.id] !== false}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                >
                  {displayName}
                </DropdownMenuCheckboxItem>
              )
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const renderColumnSettingsItems = (includeResetAll = false) => {
    const activeDateTimeLabel = !dateTimeToggleValue ? dateTimeToggleLabel : 'Nur Datum'

    return (
      <>
        {showDateTimeToggle && (
          <DropdownMenuItem
            key="date-time-toggle"
            className="justify-between"
            onSelect={(event) => event.preventDefault()}
          >
            <span>{activeDateTimeLabel}</span>
            <Switch
              checked={dateTimeToggleValue}
              onCheckedChange={onDateTimeToggleChange}
              disabled={!onDateTimeToggleChange}
            />
          </DropdownMenuItem>
        )}
        {enableColumnReordering && (
          <DropdownMenuItem
            onClick={() => {
              setInternalColumnOrder([])
              onColumnOrderChange?.([])
            }}
          >
            Reihenfolge zurücksetzen
          </DropdownMenuItem>
        )}
        {enableColumnResizing && (
          <DropdownMenuItem
            onClick={() => {
              setColumnSizing({})
              onColumnSizingChange?.({})
            }}
          >
            Größe zurücksetzen
          </DropdownMenuItem>
        )}
        {includeResetAll && (
          <DropdownMenuItem
            onClick={() => {
              setInternalColumnOrder([])
              onColumnOrderChange?.([])
              setColumnSizing({})
              onColumnSizingChange?.({})
            }}
          >
            Alle Einstellungen zurücksetzen
          </DropdownMenuItem>
        )}
      </>
    )
  }

  // Render helper: Column settings control
  const renderColumnSettings = () => {
    if (!enableColumnResizing && !enableColumnReordering) return null
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="!shadow-none">
            <Settings className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">{renderColumnSettingsItems()}</DropdownMenuContent>
      </DropdownMenu>
    )
  }

  // Render helper: Page size selector
  const renderPageSize = () => {
    if (
      !pageSizeOptions ||
      pageSizeOptions.length === 0 ||
      !onPageSizeChange ||
      positions.pageSize === 'none'
    )
      return null
    return (
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Zeilen pro Seite:</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
        >
          <SelectTrigger className="h-8 w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  // Render helper: Total count
  const renderTotalCount = () => {
    if (positions.totalCount === 'none') return null
    return (
      <div className="text-muted-foreground text-sm">
        {manualPagination && totalCount !== undefined
          ? totalCount
          : table.getFilteredRowModel().rows.length}{' '}
        {(manualPagination && totalCount !== undefined
          ? totalCount
          : table.getFilteredRowModel().rows.length) > 1
          ? 'Einträge'
          : 'Eintrag'}{' '}
        insgesamt
      </div>
    )
  }

  // Render helper: Pagination controls
  const renderPagination = () => {
    if (!showPagination || positions.pagination === 'none') return null
    return (
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage() || isFetching || isPending}
          loading={isFetching || isPending}
        >
          Zurück
        </Button>
        <div className="text-muted-foreground text-sm">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage() || isFetching || isPending}
          loading={isFetching || isPending}
        >
          Weiter
        </Button>
      </div>
    )
  }

  // Group controls by position
  const topLeftControls = [
    positions.search === 'top-left' && renderSearch(),
    positions.columnVisibility === 'top-left' && renderColumnVisibility(),
    positions.columnSettings === 'top-left' && renderColumnSettings(),
    positions.pageSize === 'top-left' && renderPageSize(),
    positions.totalCount === 'top-left' && renderTotalCount(),
    positions.pagination === 'top-left' && renderPagination(),
  ].filter(Boolean)

  const topRightControls = [
    positions.search === 'top-right' && renderSearch(),
    positions.totalCount === 'top-right' && renderTotalCount(),
    positions.pageSize === 'top-right' && renderPageSize(),
    positions.pagination === 'top-right' && renderPagination(),
    positions.columnVisibility === 'top-right' && renderColumnVisibility(),
    positions.columnSettings === 'top-right' && renderColumnSettings(),
  ].filter(Boolean)

  const bottomLeftControls = [
    positions.totalCount === 'bottom-left' && renderTotalCount(),
    positions.pageSize === 'bottom-left' && renderPageSize(),
    positions.columnVisibility === 'bottom-left' && renderColumnVisibility(),
    positions.columnSettings === 'bottom-left' && renderColumnSettings(),
  ].filter(Boolean)

  const bottomRightControls = [
    positions.pagination === 'bottom-right' && renderPagination(),
    positions.columnVisibility === 'bottom-right' && renderColumnVisibility(),
    positions.columnSettings === 'bottom-right' && renderColumnSettings(),
  ].filter(Boolean)

  // Create controls object for customHeader function (always render controls for custom header, ignore positions)
  const controls: DataTableControls = React.useMemo(
    () => ({
      search:
        searchKeys && searchKeys.length > 0 ? (
          <div className="relative w-full max-w-sm">
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value)
                if (pagination.pageIndex !== 0) {
                  table.setPageIndex(0)
                }
              }}
              className="w-full"
            />
            {(isFetching || isPending) && searchValue.length >= 1 && (
              <Loader2 className="text-muted-foreground absolute right-2 top-2 h-4 w-4 animate-spin" />
            )}
          </div>
        ) : null,
      columnVisibility: showColumnVisibility ? (
        <DropdownMenu
          onOpenChange={(open) => {
            if (open) {
              setInitialColumnVisibilityState({ ...columnVisibility })
            } else if (onColumnVisibilityChange) {
              const hasChanged =
                JSON.stringify(initialColumnVisibilityState) !== JSON.stringify(columnVisibility)
              if (hasChanged) {
                onColumnVisibilityChange(columnVisibility)
              }
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="!shadow-none">
              {columnVisibilityButtonText} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={columnVisibilityDropdownClassName}>
            {table
              .getAllColumns()
              .filter((column) => !EXCLUDED_VISIBILITY_COLUMNS.includes(column.id as any))
              .map((column) => {
                const displayName = getColumnDisplayName(column)
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={columnVisibility[column.id] !== false}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {displayName}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null,
      columnSettings:
        enableColumnResizing || enableColumnReordering ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="!shadow-none">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">{renderColumnSettingsItems(true)}</DropdownMenuContent>
          </DropdownMenu>
        ) : null,
      pageSize:
        pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Zeilen pro Seite:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null,
      pagination: showPagination ? (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (manualPagination && onPageChange) {
                onPageChange(pagination.pageIndex - 1)
              } else {
                table.previousPage()
              }
            }}
            disabled={!table.getCanPreviousPage()}
          >
            Zurück
          </Button>
          <div className="flex items-center gap-1">
            <div className="text-muted-foreground text-sm">Seite</div>
            <span className="text-sm">
              {pagination.pageIndex + 1} von {table.getPageCount() || 1}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (manualPagination && onPageChange) {
                onPageChange(pagination.pageIndex + 1)
              } else {
                table.nextPage()
              }
            }}
            disabled={!table.getCanNextPage()}
          >
            Weiter
          </Button>
        </div>
      ) : null,
      totalCount: (
        <div className="text-muted-foreground text-sm">
          {totalCount !== undefined
            ? `Zeige ${pagination.pageIndex * pageSize + 1}-${Math.min((pagination.pageIndex + 1) * pageSize, totalCount)} von ${totalCount} Einträgen`
            : `Zeige ${table.getFilteredRowModel().rows.length} Einträgen`}
        </div>
      ),
    }),
    [
      searchKeys,
      searchPlaceholder,
      searchValue,
      pagination.pageIndex,
      table,
      isFetching,
      isPending,
      showColumnVisibility,
      columnVisibility,
      onColumnVisibilityChange,
      columnVisibilityButtonText,
      columnVisibilityDropdownClassName,
      pageSizeOptions,
      onPageSizeChange,
      pageSize,
      showPagination,
      manualPagination,
      onPageChange,
      totalCount,
    ]
  )

  // Resolve customHeader - can be ReactNode or function
  const resolvedCustomHeader =
    typeof customHeader === 'function' ? customHeader(controls) : customHeader

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag (prevents accidental drags)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const headerIds =
    table
      .getHeaderGroups()
      .at(0)
      ?.headers.map((h) => h.id) || []

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && enableColumnReordering) {
      const oldIndex = headerIds.indexOf(active.id as string)
      const newIndex = headerIds.indexOf(over.id as string)

      if (oldIndex !== -1 && newIndex !== -1) {
        // Don't allow reordering of fixed columns
        if (active.id === 'state' || active.id === 'edit') {
          return
        }

        // Don't allow dropping on fixed positions
        if (over.id === 'state' || over.id === 'edit') {
          return
        }

        // Separate fixed and reorderable columns
        const hasState = headerIds.includes('state')
        const hasEdit = headerIds.includes('edit')
        const reorderableColumns = headerIds.filter((id) => id !== 'state' && id !== 'edit')

        // Adjust indices to account for fixed columns
        const adjustedOldIndex = oldIndex - (hasState && oldIndex > 0 ? 1 : 0)
        const adjustedNewIndex = newIndex - (hasState && newIndex > 0 ? 1 : 0)

        // Reorder only the middle columns
        const reorderedMiddle = arrayMove(reorderableColumns, adjustedOldIndex, adjustedNewIndex)

        // Reconstruct the full order with fixed positions
        const newOrder = []
        if (hasState) newOrder.push('state')
        newOrder.push(...reorderedMiddle)
        if (hasEdit) newOrder.push('edit')

        // Update local state and call parent callback
        updateColumnOrder(newOrder)
      }
    }
  }

  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Custom header - renders independently of layout mode */}
      {resolvedCustomHeader}

      {/* Top controls bar */}
      {(topLeftControls.length > 0 || topRightControls.length > 0) && (
        <div
          className={cn(
            'flex flex-col items-start gap-2 md:flex-row md:items-center',
            controlsLayout === 'compact'
              ? 'bg-background sticky top-0 z-10 -mx-4 border-b px-4 pb-4 pt-2 md:gap-4'
              : 'md:justify-between'
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            {topLeftControls.map((control, index) => (
              <React.Fragment key={`top-left-${index}`}>{control}</React.Fragment>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {topRightControls.map((control, index) => (
              <React.Fragment key={`top-right-${index}`}>{control}</React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className={cn('bg-card rounded-lg border shadow-sm', tableWrapperClassName)}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className={cn('overflow-x-auto overflow-y-scroll')}>
            <Table
              className={cn(enableColumnResizing ? 'table-fixed' : 'table-auto', tableClassName)}
              style={enableColumnResizing ? { width: '100%' } : undefined}
            >
              <TableHeader>
                <SortableContext items={headerIds}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className="hover:bg-transparent">
                      {headerGroup.headers.map((header) => (
                        <HeaderCell
                          key={header.id}
                          header={header}
                          enableColumnReordering={enableColumnReordering}
                          enableColumnResizing={enableColumnResizing}
                          isColumnSortable={!EXCLUDED_SORTABLE_COLUMNS.includes(header.id as any)}
                        />
                      ))}
                    </TableRow>
                  ))}
                </SortableContext>
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows?.length ? (
                  <>
                    {table.getRowModel().rows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={() => onRowClick?.(row.original)}
                        className={cn(
                          'h-[57px] max-h-[57px]',
                          index === table.getRowModel().rows.length - 1 && 'border-b',
                          alternateRowColors && index % 2 === 0 && 'bg-muted/70',
                          alternateRowColors && index % 2 !== 0 && 'bg-background',
                          alternateRowColors &&
                            'transition-colors duration-150 hover:bg-gray-600/10',
                          rowClassName?.(row.original)
                        )}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className="box-border overflow-hidden text-ellipsis whitespace-nowrap"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                ) : isInitialLoad ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-muted-foreground">Daten werden geladen...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : emptyState ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {emptyState}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      <div className="text-center">
                        <p className="text-muted-foreground mb-1 text-lg font-medium">
                          Keine Daten vorhanden
                        </p>
                        <p className="text-muted-foreground text-sm">
                          Für den ausgewählten Zeitraum und Filter wurden keine Daten gefunden.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DndContext>
      </div>

      {/* Bottom controls bar */}
      {(bottomLeftControls.length > 0 || bottomRightControls.length > 0) && (
        <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            {bottomLeftControls.map((control, index) => (
              <React.Fragment key={`bottom-left-${index}`}>{control}</React.Fragment>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {bottomRightControls.map((control, index) => (
              <React.Fragment key={`bottom-right-${index}`}>{control}</React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Custom footer */}
      {customFooter}
    </div>
  )
}
